import { useHistory } from "react-router-dom";
import { IonPage, IonContent } from "@ionic/react";
import {
  Bot,
  CalendarClock,
  CodeXml,
  Home,
  LayoutGrid,
  LogOut,
  SlidersHorizontal,
  Users,
} from "lucide-react";
import { useAuth } from "../hooks/useAuthStore";
import { useStore } from "../store/useStore";
import { supabase } from "../lib/supabase";
import { PageHeader } from "../components/ui/PageHeader";
import { Avatar } from "../components/ui/Avatar";
import { Badge } from "../components/ui/Badge";
import { IconChip, ListGroup, ListRow } from "../components/ui/ListRow";
import { getRoleBadge } from "../logic/households";

export default function SettingsPage() {
  const history = useHistory();
  const { user } = useAuth();

  const currentHouseholdId = useStore((s) => s.currentHouseholdId);
  const households = useStore((s) => s.households);
  const userHouseholds = useStore((s) => s.userHouseholds);
  const userProfile = useStore((s) => s.userProfile);

  const currentHousehold = households.find((h) => h.uuid === currentHouseholdId);

  const currentUserHousehold = userHouseholds.find(
    (uh) => uh.household_uuid === currentHouseholdId && uh.user_uuid === user?.id
  );
  const isCleaner = currentUserHousehold?.role === "cleaner";
  const roleBadge = getRoleBadge(
    currentUserHousehold?.role,
    !!user?.id && currentHousehold?.created_by_uuid === user.id
  );

  const handleLogout = async () => {
    await supabase.auth.signOut();
    history.push("/login");
  };

  const displayName = userProfile?.name || "User";

  return (
    <IonPage>
      <PageHeader title="Settings" />
      <IonContent>
        <div className="max-w-lg mx-auto px-4 pt-2 pb-28 space-y-6">
          {/* User Profile */}
          <section aria-labelledby="profile-heading">
            <h2
              id="profile-heading"
              className="px-1 mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground"
            >
              Profile
            </h2>
            <div className="card-surface p-4 flex items-center gap-4">
              <Avatar
                name={displayName}
                seed={user?.id}
                photoUrl={userProfile?.profile_photo_url}
                size="xl"
              />
              <div className="min-w-0 flex-1">
                <div className="text-lg font-bold text-foreground truncate">{displayName}</div>
                <div className="text-sm text-muted-foreground truncate">{user?.email}</div>
                {currentHousehold && (
                  <div className="mt-1.5 flex items-center gap-2 min-w-0">
                    <span className="text-[13px] font-medium text-foreground/80 truncate">
                      {currentHousehold.name}
                    </span>
                    <Badge tone={roleBadge.tone}>{roleBadge.label}</Badge>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Household group (hidden for cleaners) */}
          {!isCleaner && currentHousehold && (
            <ListGroup title="Household">
              <ListRow
                title="Weekly Effort Schedule"
                subtitle="How much you can do each day"
                leading={
                  <IconChip tone="primary">
                    <CalendarClock className="w-5 h-5" />
                  </IconChip>
                }
                onClick={() => history.push("/settings/effort-schedule")}
              />
              <ListRow
                title="Household Settings"
                subtitle="Name and invite link"
                leading={
                  <IconChip tone="success">
                    <Home className="w-5 h-5" />
                  </IconChip>
                }
                onClick={() => history.push("/settings/household")}
              />
              <ListRow
                title="Manage Areas"
                subtitle="Rooms and spaces"
                leading={
                  <IconChip tone="warning">
                    <LayoutGrid className="w-5 h-5" />
                  </IconChip>
                }
                onClick={() => history.push("/settings/areas")}
              />
              <ListRow
                title="Members"
                subtitle="Roles and invitations"
                leading={
                  <IconChip tone="purple">
                    <Users className="w-5 h-5" />
                  </IconChip>
                }
                onClick={() => history.push("/settings/members")}
              />
            </ListGroup>
          )}

          {/* Preferences group (hidden for cleaners) */}
          {!isCleaner && (
            <ListGroup title="Preferences">
              <ListRow
                title="App Settings"
                subtitle="Gamification and more"
                leading={
                  <IconChip>
                    <SlidersHorizontal className="w-5 h-5" />
                  </IconChip>
                }
                onClick={() => history.push("/settings/app")}
              />
            </ListGroup>
          )}

          {/* AI assistants (MCP) — available to every role */}
          <ListGroup title="AI Assistants">
            <ListRow
              title="AI Assistants"
              subtitle="Connect Claude or ChatGPT"
              leading={
                <IconChip tone="primary">
                  <Bot className="w-5 h-5" />
                </IconChip>
              }
              onClick={() => history.push("/settings/ai-assistants")}
            />
          </ListGroup>

          {/* Developer group (hidden for cleaners) */}
          {!isCleaner && (
            <ListGroup title="Developer">
              <ListRow
                title="Developer Tools"
                leading={
                  <IconChip>
                    <CodeXml className="w-5 h-5" />
                  </IconChip>
                }
                onClick={() => history.push("/settings/developer")}
              />
            </ListGroup>
          )}

          {/* Account */}
          <div className="card-surface overflow-hidden">
            <ListRow
              title="Sign Out"
              destructive
              hideChevron
              leading={
                <IconChip tone="danger">
                  <LogOut className="w-5 h-5" />
                </IconChip>
              }
              onClick={handleLogout}
            />
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
